import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { useUsers } from "../hooks/useUsers.js";
import { Card, EmptyState, Field, Input, Select, Spinner, Button } from "../components/ui.js";
import { formatDate } from "../lib/format.js";

interface AuditRow {
  id: string;
  userId: string | null;
  username: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  createdAt: string;
}

const PAGE_SIZE = 50;

export function AuditPage() {
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const usersQuery = useUsers({ limit: 100 });

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (action) params.set("action", action);
  if (entityType.trim()) params.set("entityType", entityType.trim());
  if (entityId.trim()) params.set("entityId", entityId.trim());
  if (userId) params.set("userId", userId);
  if (from) params.set("from", new Date(`${from}T00:00:00`).toISOString());
  if (to) params.set("to", new Date(`${to}T23:59:59.999`).toISOString());

  const query = useQuery({
    queryKey: ["audit", action, entityType, entityId, userId, from, to, offset],
    queryFn: () => api.get<{ data: AuditRow[] }>(`/audit?${params}`).then((response) => response.data),
  });

  function updateFilter(setter: (value: string) => void, value: string) {
    setter(value);
    setOffset(0);
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Audit history</h1>
        <p className="text-sm text-slate-400">Administrative changes, newest first. Credential fields are redacted.</p>
      </div>

      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Action">
            <Input value={action} onChange={(event) => updateFilter(setAction, event.target.value)} placeholder="create, update, delete…" />
          </Field>
          <Field label="Actor">
            <Select value={userId} onChange={(event) => updateFilter(setUserId, event.target.value)}>
              <option value="">All users</option>
              {(usersQuery.data?.data ?? []).map((user) => <option key={user.id} value={user.id}>{user.username}</option>)}
            </Select>
          </Field>
          <Field label="Entity type">
            <Input value={entityType} onChange={(event) => updateFilter(setEntityType, event.target.value)} placeholder="item, user, category…" />
          </Field>
          <Field label="Entity ID">
            <Input value={entityId} onChange={(event) => updateFilter(setEntityId, event.target.value)} placeholder="UUID" />
          </Field>
          <Field label="From date">
            <Input type="date" value={from} onChange={(event) => updateFilter(setFrom, event.target.value)} />
          </Field>
          <Field label="To date">
            <Input type="date" value={to} onChange={(event) => updateFilter(setTo, event.target.value)} />
          </Field>
        </div>
      </Card>

      {query.isError ? <Card><p className="text-sm text-rose-300">Could not load audit history: {(query.error as Error).message}</p></Card> : null}
      {query.isError ? null : query.isLoading ? <Spinner label="Loading audit history…" /> : query.data?.length ? (
        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-400">
                <tr><th className="p-3">When</th><th>Actor</th><th>Action</th><th>Entity</th><th>Changes</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {query.data.map((row) => (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap p-3 text-slate-400">{formatDate(row.createdAt)}</td>
                    <td>{row.username ?? "System/agent"}</td>
                    <td>{row.action}</td>
                    <td>{row.entityType}{row.entityId ? <div className="font-mono text-xs text-slate-400">{row.entityId}</div> : null}</td>
                    <td className="max-w-xl p-3"><pre className="whitespace-pre-wrap break-words text-xs text-slate-300">{row.changes ? JSON.stringify(row.changes, null, 2) : "—"}</pre></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : <EmptyState title="No audit entries match these filters" />}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
        <span>{query.data?.length ? `${offset + 1}–${offset + query.data.length}${query.data.length === PAGE_SIZE ? "+" : ""}` : "0 entries"}</span>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={offset === 0 || query.isFetching} onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}>Previous</Button>
          <Button variant="secondary" disabled={(query.data?.length ?? 0) < PAGE_SIZE || query.isFetching} onClick={() => setOffset((value) => value + PAGE_SIZE)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
